import { FormControl, FormErrorMessage } from '@chakra-ui/form-control'
import { Input } from '@chakra-ui/input'
import { Text as ChakraText, VStack } from '@chakra-ui/layout'
import {
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
} from '@chakra-ui/modal'
import { Button, Icon } from '@chakra-ui/react'
import type { FieldProps } from 'formik'
import { Field, Formik } from 'formik'
import React from 'react'
import { useTranslation } from 'react-i18next'
import { RiDeleteBinLine } from 'react-icons/ri'
import type { Req } from 'use-http'
import * as Yup from 'yup'
import { encodeHash } from '../../../../utils'
import useToast from '../../../hooks/useToast'
import type { UserSessionResponse } from '../../../interfaces'
import type SettingsResponse from '../../../interfaces/SettingsResponse'

interface Props {
  currentNamedTransform?: string | null
  values: SettingsResponse
  user: UserSessionResponse | null
  isOpen: boolean
  request: Req
  getData(): Promise<void>
  onClose(): void
}

interface FormProps {
  label: string
  confirmLabel: string
}

const NamedTransformsDeleteModal = ({
  isOpen,
  onClose,
  currentNamedTransform,
  user,
  values,
  request,
  getData,
}: Props): React.ReactElement | null => {
  const { t } = useTranslation()
  const toast = useToast()
  if (
    !(
      currentNamedTransform &&
      Object.keys(values.namedTransform).includes(currentNamedTransform)
    )
  ) {
    return null
  }

  const handleSubmit = async (): Promise<void> => {
    if (user?.clientNumber) {
      await request.delete(
        `api/clients/${user.clientNumber}/named-transformations/${encodeHash(currentNamedTransform)}`,
      )

      if (request.error) {
        toast({
          description: t('something_went_wrong', 'Something went wrong!'),
          status: 'error',
          isClosable: true,
          duration: 5000,
          position: 'top-right',
          variant: 'outline',
        })
        return
      }
      await getData()
      toast({
        description: t(
          'successfully_deleted_transform',
          'Successfully deleted named transform',
        ),
        status: 'success',
        isClosable: true,
        duration: 5000,
        position: 'top-right',
        variant: 'outline',
      })
      onClose()
    }
  }

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="xl">
      <ModalOverlay />
      <Formik<FormProps>
        validationSchema={Yup.object().shape({
          label: Yup.string().required('Required'),
          confirmLabel: Yup.string()
            .required('Required')
            .oneOf(
              [Yup.ref('label')],
              "The submitted value doesn't match with original name",
            ),
        })}
        initialValues={{
          label: values.namedTransform[currentNamedTransform].label,
          confirmLabel: '',
        }}
        onSubmit={handleSubmit}
      >
        {({ submitForm, errors, touched }) => (
          <ModalContent>
            <ModalHeader>
              <Icon
                as={RiDeleteBinLine}
                color="appBattleshipGrey"
                backgroundColor="appPaleGrey"
                boxSize={6}
                padding="0.1em"
                borderRadius="full"
                marginRight="0.5em"
              />
              {t('delete_named_transform', 'Delete Named Transform')}
            </ModalHeader>
            <ModalCloseButton />
            <ModalBody>
              <VStack spacing="2em">
                <Field name="label">
                  {({ field }: FieldProps<string, FormProps>) => (
                    <FormControl marginTop="1em" isRequired>
                      <ChakraText marginBottom="0.5em" color="appBattleshipGrey">
                        {t(
                          'delete_named_transform_confirmation_pre',
                          'This will permanently delete the named transform: ',
                        )}
                        <b>{field.value}</b> <br />
                        {t(
                          'delete_named_transform_confirmation_post',
                          'Type in the name of the named transform to confirm.',
                        )}
                      </ChakraText>
                    </FormControl>
                  )}
                </Field>
                <Field name="confirmLabel">
                  {({ field }: FieldProps<string, FormProps>) => (
                    <FormControl
                      marginTop="1em"
                      isRequired={true}
                      isInvalid={Boolean(
                        errors.confirmLabel && touched.confirmLabel,
                      )}
                    >
                      <Input {...field} />
                      <FormErrorMessage marginBottom="-2.0408em">
                        {errors.confirmLabel}
                      </FormErrorMessage>
                    </FormControl>
                  )}
                </Field>
              </VStack>
            </ModalBody>
            <ModalFooter borderTop="1px solid #e7eaf0" style={{ gap: '1em' }}>
              <Button onClick={onClose} variant="outline">
                {t('cancel')}
              </Button>
              <Button type="submit" colorScheme="appRed" onClick={submitForm}>
                {t('submit')}
              </Button>
            </ModalFooter>
          </ModalContent>
        )}
      </Formik>
    </Modal>
  )
}

export default NamedTransformsDeleteModal
